import isEqual from 'react-fast-compare';
import type { Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { ChatModalProvider } from '@/contexts/chatModal';
import type {
  ChatMediaInfoPartial,
  UseChatMediaCallResult,
} from '@/contexts/chat/scripts/hooks/useChatMediaCall/types';
import type { ChatContextHooksFull } from '@/contexts/chat/context';
import { useChatContext } from '@/contexts/chat/context';
import { useReply } from '@/scripts/hooks/useReply';
import { useContactsContext } from '@/contexts/contacts/context';
import ChatFooter from './components/chat-footer';
import ChatHeader from './components/chat-header';
import ChatMessages from './components/chat-messages';
import ChatPinned from './components/chat-pinned';
import classes from './index.module.scss';
import { EditorContext } from './components/chat-editor';

export interface ChatProps {
  className?: ClassNames;
  chatGroup: ChatContextHooksFull['chatGroup'];
  chat: Chat;
  chatMediaCall: UseChatMediaCallResult;
  mediaJoin: (chatMediaInfo?: ChatMediaInfoPartial) => Promise<void>;
}

function ChatMain({ className, chat, chatMediaCall, mediaJoin, chatGroup }: ChatProps) {
  const {
    fileMessages: { pickFilesUtil, getPickInfoEventHandlers },
  } = useChatContext();
  const { contacts, addContacts } = useContactsContext();
  const { replyMessage, setReplyMessage } = useReply();

  const eventHandlers = useMemo(
    () => getPickInfoEventHandlers(chat),
    [chat, getPickInfoEventHandlers]
  );

  return (
    <ChatModalProvider>
      <EditorContext>
        <FullBox
          className={cls(classes.chatMain, className)}
          frontChildren={<ChatHeader chat={chat}></ChatHeader>}
          column
        >
          <FullBox
            frontChildren={
              <ChatPinned
                chat={chat}
                contacts={contacts}
                addContacts={addContacts}
                chatGroup={chatGroup}
                chatMediaCall={chatMediaCall}
                mediaJoin={mediaJoin}
              ></ChatPinned>
            }
            column
          >
            <FullBox
              className={cls(classes.chatMessages, className)}
              centerClassName={cls('scrollbar_hover', classes.centerChatMessages)}
              rearChildren={
                <ChatFooter
                  chat={chat}
                  replyMessage={replyMessage}
                  setReplyMessage={setReplyMessage}
                ></ChatFooter>
              }
              column
              centerProps={{
                ...eventHandlers.drop,
                ...eventHandlers.paste,
              }}
            >
              <ChatMessages
                chat={chat}
                contacts={contacts}
                replyMessage={replyMessage}
                setReplyMessage={setReplyMessage}
              ></ChatMessages>
              {pickFilesUtil.isDragging && (
                <div className={classes.dropWrapper}>
                  <div className={classes.dropContent}>
                    <div className={classes.text}>
                      Drag and drop here to send to{chat.name}
                    </div>
                  </div>
                </div>
              )}
            </FullBox>
          </FullBox>
        </FullBox>
      </EditorContext>
    </ChatModalProvider>
  );
}

export default memo(ChatMain, isEqual);
